<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="
      referencesAwaitingYouScreeningConnection
    "
    :pending-connection-query="
      $apollo.queries.referencesAwaitingYouScreeningConnection
    "
    :history-connection="referencesByAwaitingYouVoteHistoryConnection"
    :history-connection-query="
      $apollo.queries.referencesByAwaitingYouVoteHistoryConnection
    "
    :search-input.sync="searchInput"
    :revoke-vote-func="revokeVote"
    :get-vote-by-current-user-func="getVoteByCurrentUser"
    :filtering-input="filteringInput"
    restrict-to-voting-only
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <screen-tab-awaiting-you-empty-state />
    </template>
  </screening-workflow-panel>
</template>

<script>
import ScreeningWorkflowPanel from './ScreeningWorkflowPanel';
import ScreenTabAwaitingYouEmptyState from './ScreenTabAwaitingYouEmptyState';
import {
  GetReferencesAwaitingYouScreening,
  GetReferencesByAwaitingYouScreeningVoteHistory,
} from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;

export default {
  name: 'ScreenTabAwaitingYou',
  components: {
    ScreeningWorkflowPanel,
    ScreenTabAwaitingYouEmptyState,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    referencesAwaitingYouScreeningConnection: null,
    referencesByAwaitingYouVoteHistoryConnection: null,
  }),
  apollo: {
    referencesAwaitingYouScreeningConnection: {
      query: GetReferencesAwaitingYouScreening,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesAwaitingYouScreening
        ),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    referencesByAwaitingYouVoteHistoryConnection: {
      query: GetReferencesByAwaitingYouScreeningVoteHistory,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesByAwaitingYouScreeningVoteHistory
        ),
      skip() {
        return (
          !this.review?.id ||
          !this.referencesAwaitingYouScreeningConnection
        );
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (active && this.referencesAwaitingYouScreeningConnection) {
        this.$apollo.queries.referencesAwaitingYouScreeningConnection.refetch();
        this.$apollo.queries.referencesByAwaitingYouVoteHistoryConnection.refetch();
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesAwaitingYouScreeningConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      revokeVote: 'reviewerScreeningVote/deleteWithCacheUpdate',
    }),
    getVoteByCurrentUser(reference) {
      return reference?.getScreeningVoteBy(this.currentResearcher);
    },
  },
};
</script>
